import { Images } from "@/constants/images"
import { t } from "@/languages/i18n"
import { getLocalizedHref } from "@/lib/locale-utils"
import { SiteId } from "@/sites/config"
import { formatDate } from "@/utils/formatDate"
import Image from "next/image"
import Link from "next/link"

type PostHeaderProps = {
  postTitle: string
  categories: Array<{
    id: string | number
    attributes?: {
      name?: string
      slug?: string
    }
  }>
  author: string
  publishedAt?: string
  siteId?: SiteId
  description?: string
  isLiveHappening?: boolean
  locale?: string
}

export default function PostHeader({
  postTitle,
  categories,
  author,
  publishedAt,
  siteId,
  description = "",
  isLiveHappening = false,
  locale,
}: PostHeaderProps) {
  const isTamdaOne = siteId === "tamdagroup.eu"
  const formattedDate = publishedAt ? formatDate(publishedAt) : null

  // Layout cho tamdagroup.eu: metadata trên 1 dòng, title lớn bên dưới
  if (isTamdaOne) {
    return (
      <>
        <div className="flex sm:flex-row flex-col sm:justify-between sm:items-center gap-2 sm:gap-0 mb-4">
          <nav aria-label={t("Danh mục bài viết")} className="flex items-center gap-2 sm:gap-4 flex-wrap">
            <span className="text-sm text-public-fg font-bold">{t("Danh mục")}:</span>
            {categories.length > 0 &&
              categories.map((cat, index) => (
                <span key={cat.id} className="flex items-center">
                  <Link
                    href={getLocalizedHref(locale, `/category/${cat.attributes?.slug || ""}`)}
                    className="text-sm text-public-brand-cta leading-[21.7px]"
                  >
                    {cat.attributes?.name}
                  </Link>
                  {index < categories.length - 1 && <span className="text-public-fg">,</span>}
                </span>
              ))}
          </nav>
          <div className="text-sm text-public-fg-subtle italic">
            {t("Đăng lúc")}: {publishedAt && <time dateTime={publishedAt}>{formattedDate?.formattedDate || ""}</time>},{" "}
            {author}
          </div>
        </div>
        <div className="flex items-baseline gap-2">
          {isLiveHappening && (
            <span className="live-pill inline-flex items-center gap-1 rounded-[14px] px-2 py-[2px] text-xs font-bold  text-public-brand-live">
              <span className="live-dot h-3 w-3 rounded-full bg-white" />
              <span className="live-text normal-case">Live</span>
            </span>
          )}
          {/* Post title is the page's h1. Previously this was h2, which left
           * the page without a top-level heading and confused screen readers
           * + SEO outline. */}
          <h1 className="sm:text-[2rem] sm:leading-[49.6px] text-[25.6px] leading-[39.68px] font-black text-public-fg">
            {postTitle}
          </h1>
        </div>
      </>
    )
  }

  // Layout cho tamdamedia.eu và các site khác
  return (
    <>
      <div className="flex items-center gap-2">
        {isLiveHappening && (
          <span className="live-pill inline-flex items-center gap-1.5 rounded-[14px] px-2 py-[2px] text-sm font-bold text-public-brand-live">
            <span className="live-dot h-3 w-3 rounded-full" />
            <span className="live-text normal-case">Live</span>
          </span>
        )}
        <h1 className="sm:text-2xl sm:leading-[1.55] text-[22px] leading-[34.1px] font-black text-public-fg">
          {postTitle}
        </h1>
      </div>
      {/* Category links — a nav landmark, not a heading. Previously wrapped
       * in <h2> which injected a second-level heading that duplicated the
       * real content structure and broke the outline. */}
      {categories.length > 0 && (
        <nav aria-label={t("Danh mục bài viết")} className="text-base text-public-brand leading-relaxed mb-4">
          {categories.map((cat, index) => (
            <span key={cat.id}>
              <Link
                href={getLocalizedHref(locale, `/category/${cat.attributes?.slug || ""}`)}
                className="text-base font-medium transition-colors hover:text-public-brand-hover"
              >
                {cat.attributes?.name}
              </Link>
              {index < categories.length - 1 && <span className="text-public-brand">, </span>}
            </span>
          ))}
        </nav>
      )}
      {/* Author + publish time — plain text, not a heading. Date wrapped in
       * <time> so assistive tech + search can parse the ISO timestamp. */}
      <div className="flex gap-2 sm:text-base sm:leading-[24.8px] text-sm leading-[21.7px] font-normal sm:font-medium text-public-fg-primary items-center">
        <Image src={Images.authorIcon} alt="" aria-hidden="true" className="w-3 h-3" />
        <span>
          {author}, {publishedAt && <time dateTime={publishedAt}>{formattedDate?.formattedDateTime || ""}</time>}
        </span>
      </div>
      {description && isLiveHappening && (
        <p className="mt-3 text-sm leading-7 font-bold text-public-fg">{description}</p>
      )}
    </>
  )
}
